import React, { useMemo } from 'react';

import { CustomOperatorValue, WORKFLOW_FILTER_FIELDS } from '@aline/common';
import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { ChartPie, Flag, LayoutTemplate, Signature, TagIcon, UserRoundIcon } from 'lucide-react';

import { FilterOption } from './FilterOption';
import type { WorkflowFilterChipId, WorkflowFilterConfig } from './workflow-filter-config';
import type { WorkflowQuickFilterItem } from './WorkflowQuickFilters';
import { WorkflowQuickFilters } from './WorkflowQuickFilters';

const ALL_FILTERS: WorkflowFilterConfig[] = [
  {
    field: WORKFLOW_FILTER_FIELDS.workflowType,
    icon: <LayoutTemplate size={16} />,
    id: 'workflow-type',
    label: 'Workflow type',
    operator: CustomOperatorValue.WorkflowType,
    type: 'workflowType',
  },
  {
    field: 'state',
    icon: <Signature size={16} />,
    id: 'signing-status',
    label: 'Signing Status',
    operator: CustomOperatorValue.WorkflowState,
    type: 'signingStatus',
  },
  {
    field: 'column-category',
    icon: <ChartPie height={16} width={16} />,
    id: 'status-type',
    label: 'Status Type',
    operator: 'isAnyOf',
    type: 'status',
  },
  {
    field: 'owner',
    icon: <UserRoundIcon height={16} width={16} />,
    id: 'requester',
    label: 'Requester',
    operator: CustomOperatorValue.Owner,
    type: 'requester',
  },
  {
    field: 'priority',
    icon: <Flag size={16} />,
    id: 'priority',
    label: 'Priority',
    operator: CustomOperatorValue.WorkflowPriority,
    type: 'priority',
  },
  {
    field: 'permissions',
    icon: <UserRoundIcon height={16} width={16} />,
    id: 'assignee',
    label: 'Assignee',
    operator: CustomOperatorValue.WorkflowAssigneeAnyOf,
    type: 'assignee',
  },
  {
    field: 'labels',
    icon: <TagIcon height={16} width={16} />,
    id: 'labels',
    label: 'Labels',
    operator: CustomOperatorValue.Label,
    type: 'labels',
  },
];

const DEFAULT_TOOLBAR_CHIP_IDS: WorkflowFilterChipId[] = [
  'workflow-type',
  'signing-status',
  'status-type',
  'requester',
  'priority',
  'assignee',
  'labels',
];

interface WorkflowFiltersProps {
  chipIds?: WorkflowFilterChipId[];
}

function getActiveFilterCount(value: unknown): number {
  if (!Array.isArray(value)) return 0;
  return value.length;
}

export function WorkflowFilters({
  chipIds = DEFAULT_TOOLBAR_CHIP_IDS,
}: WorkflowFiltersProps): JSX.Element {
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const items: WorkflowQuickFilterItem[] = useMemo(() => {
    const chipIdSet = new Set(chipIds);
    return ALL_FILTERS.filter(filter => chipIdSet.has(filter.id)).map(filter => ({
      activeCount: getActiveFilterCount(
        filterModel.items.find(item => item.field === filter.field)?.value
      ),
      id: filter.id,
      render: () => <FilterOption filter={filter} />,
    }));
  }, [chipIds, filterModel.items]);

  return <WorkflowQuickFilters items={items} />;
}
